import * as React from 'react';
import PropTypes from 'prop-types';
import {useLocation} from 'react-router-dom';
import {FormattedMessage} from '@meiko/react-intl';
import {twMerge} from 'tailwind-merge';
import {createReferrerUrl} from 'utils/url';
import {getListItemBuilding} from 'utils/lists';
import {LISTABLE_TYPES, LISTABLE_TYPES_MODELS} from 'dicts/lists';
import {marketingLeadStates} from 'dicts/leads';
import BuildingEncounterStateTag from 'components/generic/BuildingEncounterStateTag';
import ListItemIcon from 'views/Lists/components/ListItem/ListItemIcon';
import ListItemActionButtons from 'views/Lists/components/ListItem/ListItemActionButtons';
import ListItemTags from 'views/Lists/components/ListItem/ListItemTags';
import ListItemComments from 'views/Lists/components/ListItem/ListItemComments';
import BuildingInformation from 'views/Lists/components/ListItem/BuildingInformation';
import LeadInformation from 'views/Lists/components/ListItem/LeadInformation';
import ClientInformation from 'views/Lists/components/ListItem/ClientInformation';

const ListItemCard = React.memo(
	({listItem, intl, onProcessItem, onShowDetails, className}) => {
		const location = useLocation();
		const referrerUrl = createReferrerUrl(location);

		const itemType = LISTABLE_TYPES_MODELS[listItem?.listableType];
		const listable = listItem?.listable?.data;
		const building = getListItemBuilding(listItem);

		// Keep parity with list view icon logic. (Products/offers can be added later if needed.)
		const hasProducts = false;
		const hasOffers = false;

		if (!listItem || !listable) {
			return (
				<div
					className={twMerge(
						'rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-3',
						className,
					)}
				>
					<div className="text-sm text-gray-600 dark:text-gray-300">
						<FormattedMessage id="Content loading failed. Try refreshing the page" />
					</div>
				</div>
			);
		}

		return (
			<div
				className={twMerge(
					'rounded-md border border-gray-200 dark:border-gray-700',
					'bg-white dark:bg-gray-900',
					'hover:bg-gray-50 dark:hover:bg-gray-800/40',
					'transition-colors',
					'p-3',
					'cursor-pointer',
					className,
				)}
				onClick={onShowDetails}
			>
				<div className="flex items-start gap-3">
					<ListItemIcon
						itemType={itemType}
						hasProducts={hasProducts}
						hasOffers={hasOffers}
						className="shrink-0"
					/>

					<div className="min-w-0 flex-1 flex flex-col gap-1">
						{itemType === LISTABLE_TYPES.clients ? (
							<ClientInformation listItem={listItem} />
						) : (
							<>
								<BuildingInformation building={building} listItem={listItem} />
								{itemType === LISTABLE_TYPES.leads && (
									<LeadInformation building={building} listItem={listItem} />
								)}
							</>
						)}

						{/* State (compact) */}
						{itemType !== LISTABLE_TYPES.clients && (
							<div className="text-xs text-gray-500">
								{itemType === LISTABLE_TYPES.buildings ? (
									<BuildingEncounterStateTag building={listable} preferD2DState={true} />
								) : itemType === LISTABLE_TYPES.leads ? (
									marketingLeadStates[listable.state] ? (
										<FormattedMessage id={marketingLeadStates[listable.state]} />
									) : null
								) : null}
							</div>
						)}
					</div>
				</div>

				<div className="mt-3 flex items-center justify-between gap-3">
					<div className="flex items-center gap-2">
						<ListItemComments comments={listItem.comments ?? ''} />
						<ListItemTags tags={listable?.tags?.data} />
					</div>

					<ListItemActionButtons
						listItem={listItem}
						referrerUrl={referrerUrl}
						onProcessItem={() => onProcessItem(listItem)}
						isProcessing={false}
						className=""
					/>
				</div>
			</div>
		);
	},
);

ListItemCard.displayName = 'ListItemCard';

ListItemCard.propTypes = {
	listItem: PropTypes.object,
	intl: PropTypes.object.isRequired,
	onProcessItem: PropTypes.func.isRequired,
	onShowDetails: PropTypes.func.isRequired,
	className: PropTypes.string,
};

export default ListItemCard;
